import Heading from '@tiptap/extension-heading'
import Blockquote from '@tiptap/extension-blockquote'
import Link from '@tiptap/extension-link'
import Image from '@tiptap/extension-image'
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight'
import HorizontalRule from '@tiptap/extension-horizontal-rule'
import { ListItem } from '@tiptap/extension-list-item'
import { Paragraph } from '@tiptap/extension-paragraph'
import { Table } from '@tiptap/extension-table'
import { TableCell } from '@tiptap/extension-table-cell'
import { TableHeader } from '@tiptap/extension-table-header'
import { TableRow } from '@tiptap/extension-table-row'
import { StarterKit } from '@tiptap/starter-kit'
import { TaskList } from '@tiptap/extension-task-list'
import { TaskItem } from '@tiptap/extension-task-item'
import { Editor } from '@tiptap/react'
import {
  Extension,
  type JSONContent,
  type MarkdownRendererHelpers,
  type MarkdownToken,
  type RenderContext,
} from '@tiptap/core'
import { EditorState, Transaction } from 'prosemirror-state'
import { Fragment, Slice } from 'prosemirror-model'
import { common, createLowlight } from 'lowlight'
import { Markdown } from '@tiptap/markdown'
import { StyleType } from '../../../../utils/markdown'
import {
  CODE_FENCE_RE,
  collapseEmptyParagraphMarkers,
  demoteMarkersToBlankLines,
  normalize,
} from '../../../../utils/multilineText'
import { ActiveCellHighlight } from './TableInteractions/activeCellHighlightExtension'

const lowlight = createLowlight(common)

export const getEditorExtensions = () => {
  return [
    StarterKit.configure({
      codeBlock: false,
      heading: false,
      blockquote: false,
      horizontalRule: false,
      paragraph: false,
      listItem: false,
      link: false,
    }),
    CustomParagraph,
    CustomListItem,
    CustomHeading,
    CustomBlockquote,
    CustomLink,
    Image.configure({
      inline: true,
    }),
    CustomCodeBlock.configure({
      lowlight,
    }),
    CustomHorizontalRule,
    TaskList,
    TaskItem.configure({
      nested: true,
    }),
    CustomTable,
    TableCell,
    TableHeader,
    TableRow,
    ActiveCellHighlight,
    MarkdownEscape,
    Markdown.configure({
      markedOptions: {
        breaks: true,
      },
    }),
  ]
}

// Markdownソース編集用の最小構成。装飾系を無効化してプレーンテキスト扱いにする。
// @tiptap/markdown は含めず、editor.getText() で生マークダウン文字列として取り出す。
export const getMarkdownSourceExtensions = () => {
  return [
    StarterKit.configure({
      blockquote: false,
      bold: false,
      bulletList: false,
      code: false,
      codeBlock: false,
      heading: false,
      horizontalRule: false,
      italic: false,
      link: false,
      listItem: false,
      listKeymap: false,
      orderedList: false,
      strike: false,
      underline: false,
    }),
  ]
}

// ソースエディタの空段落非表示CSS回避用。コードブロック内の空行に入れて
// <p><br></p> にならないようにし、CSSの対象外にする。
const ZERO_WIDTH_SPACE = '​'

const CLIPBOARD_DATA_TYPE_HTML: string = 'text/html'
const HTML_ANCHOR_TAG_REGEX: string = '<a '

const isHttpUrl = (url: string): boolean => /^https?:\/\//i.test(url)

// リッチ側の insertLinkFromClipboard と同条件。
// ProseMirror 由来のペースト（data-pm-slice付き）は構造復元を優先し、
// 通常のペースト処理に委譲する。
const isLinkHtmlPaste = (html: string | undefined): html is string => {
  if (!html) return false
  return (
    new RegExp(HTML_ANCHOR_TAG_REGEX).test(html.replaceAll('\r\n', '')) &&
    !html.includes('data-pm-slice')
  )
}

// 改行を補うブロック要素。網羅は狙わず、Webページからのリンクペーストで
// 典型的に現れる要素のみ対象とする。
const HTML_BLOCK_TAGS = new Set([
  'P',
  'DIV',
  'LI',
  'TR',
  'BLOCKQUOTE',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
])

// Markdown のリンク先は半角の ) や空白で打ち切られる。SharePoint 等の URL には
// これらが生のまま残るため、( ) と空白だけをパーセントエンコードする。
const MARKDOWN_LINK_UNSAFE_CHAR_RE = /[()\s]/g
const MARKDOWN_LINK_ESCAPES: { [char: string]: string } = {
  '(': '%28',
  ')': '%29',
}

// 裸の URL を Markdown に置くと marked の自動リンクは空白・< > で終わり、
// 末尾の句読点（GFM の規則）や対応の取れない ) をリンクから外すため、
// これらを含む href は [text](href) 形式で書く必要がある
const BARE_URL_UNSAFE_RE = /[()\s<>]|[.,;:!?*_~]$/
const isUnsafeAsBareUrl = (href: string): boolean =>
  BARE_URL_UNSAFE_RE.test(href)

const encodeMarkdownLinkHref = (href: string): string =>
  href.replace(
    MARKDOWN_LINK_UNSAFE_CHAR_RE,
    char => MARKDOWN_LINK_ESCAPES[char] ?? encodeURIComponent(char)
  )

export const formatMarkdownLink = (
  text: string,
  href: string,
  title?: string
): string => {
  const encodedHref = encodeMarkdownLinkHref(href)
  if (!title) return `[${text}](${encodedHref})`
  const escapedTitle = title.replace(/[\\"]/g, char => `\\${char}`)
  return `[${text}](${encodedHref} "${escapedTitle}")`
}

// stock の renderMarkdown は href を生のまま [text](href) に埋め込むため、
// ( ) や空白を含む URL がリッチ → Markdown → リッチの往復で壊れる。
const CustomLink = Link.extend({
  renderMarkdown: (node, h) =>
    formatMarkdownLink(
      h.renderChildren(node),
      node.attrs?.href ?? '',
      node.attrs?.title ?? ''
    ),
})

// アンカーを Markdown リンク記法へ、それ以外はテキストへ変換する。
// href が http/https 以外（javascript: 等）のアンカーはテキストのみ残す。
const htmlToMarkdownLinkText = (html: string): string => {
  const body = new DOMParser().parseFromString(html, 'text/html').body
  let result = ''

  const walk = (node: ChildNode) => {
    if (node.nodeType === Node.TEXT_NODE) {
      result += node.textContent ?? ''
      return
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return

    const element = node as HTMLElement
    if (element.tagName === 'BR') {
      result += '\n'
      return
    }
    if (element.tagName === 'A') {
      const href = element.getAttribute('href') ?? ''
      const text = element.textContent ?? ''
      if (!isHttpUrl(href)) {
        result += text
      } else if (text === href && !isUnsafeAsBareUrl(href)) {
        result += href
      } else {
        result += formatMarkdownLink(text, href)
      }
      return
    }

    element.childNodes.forEach(walk)
    if (HTML_BLOCK_TAGS.has(element.tagName)) {
      result += '\n'
    }
  }

  body.childNodes.forEach(walk)
  return result
}

// 空行を保持したまま各行を段落ノードとして挿入する。
// tiptap デフォルトのペーストは空行を段落区切りとして消すため、
// markdownToSourceDoc と同じロジックで各行を段落ノードに変換する。
const insertTextAsSourceParagraphs = (
  view: import('prosemirror-view').EditorView,
  text: string
): boolean => {
  const { schema } = view.state
  const lines = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n')
  let inCodeBlock = false

  const nodes = lines.map(line => {
    if (CODE_FENCE_RE.test(line)) {
      inCodeBlock = !inCodeBlock
    }
    if (line.length > 0) {
      return schema.nodes.paragraph.create(null, [schema.text(line)])
    }
    if (inCodeBlock) {
      return schema.nodes.paragraph.create(null, [
        schema.text(ZERO_WIDTH_SPACE),
      ])
    }
    return schema.nodes.paragraph.create()
  })

  const fragment = Fragment.from(nodes)
  const slice = new Slice(fragment, 1, 1)
  view.dispatch(view.state.tr.replaceSelection(slice))
  return true
}

// ProseMirror 由来（data-pm-slice 付き）でトップレベルがすべて <p> の HTML から、
// 1段落=1行として行の配列を復元する。それ以外の HTML は対象外（null）。
const restoreLinesFromSourceEditorHtml = (
  html: string | undefined
): string[] | null => {
  if (!html || !html.includes('data-pm-slice')) return null
  const body = new DOMParser().parseFromString(html, 'text/html').body
  const children = Array.from(body.children)
  if (children.length === 0) return null
  if (children.some(el => el.tagName !== 'P')) return null
  return children.map(p => p.textContent ?? '')
}

// text/plain と HTML 由来の行を比較するための正規化。
// Windows の CF_HTML 経由では通常スペースが &nbsp;(\xA0) に変わることがあるため同一視する。
const canonicalizeForClipboardComparison = (s: string) =>
  s.replace(/\r\n/g, '\n').replace(/\xA0/g, ' ')

// ソースエディタ用ペーストハンドラ。
// リッチ側の insertLinkFromClipboard と同条件でリンク付きHTMLを検出し、
// アンカーを Markdown リンク記法に変換してから挿入する。
export const handleSourceEditorPaste = (
  view: import('prosemirror-view').EditorView,
  event: ClipboardEvent
): boolean => {
  const html = event.clipboardData?.getData(CLIPBOARD_DATA_TYPE_HTML)
  if (isLinkHtmlPaste(html)) {
    return insertTextAsSourceParagraphs(view, htmlToMarkdownLinkText(html))
  }

  const text = event.clipboardData?.getData('text/plain')
  if (!text || !text.includes('\n')) return false

  // text/plain が「HTML の段落を \n\n で連結した形」と一致する場合、
  // clipboardTextSerializer 未設定の旧画面のソースエディタからのコピーであり、
  // そのまま貼ると空行が増殖する。正確な行構造を持つ HTML 側から復元する。
  const restoredLines = restoreLinesFromSourceEditorHtml(html)
  if (
    restoredLines !== null &&
    canonicalizeForClipboardComparison(text) ===
      canonicalizeForClipboardComparison(restoredLines.join('\n\n'))
  ) {
    return insertTextAsSourceParagraphs(view, restoredLines.join('\n'))
  }

  return insertTextAsSourceParagraphs(view, text)
}

// Markdown文字列を、Markdownソースエディタ用のdocノードに変換する。
// 改行ごとに段落を作ることで、getText({ blockSeparator: '\n' }) で元の文字列に戻せる。
export const markdownToSourceDoc = (markdown: string) => {
  const lines = markdown.split('\n')
  let inCodeBlock = false
  return {
    type: 'doc',
    content: lines.map(line => {
      if (CODE_FENCE_RE.test(line)) {
        inCodeBlock = !inCodeBlock
      }
      if (line.length > 0) {
        return { type: 'paragraph', content: [{ type: 'text', text: line }] }
      }
      if (inCodeBlock) {
        return {
          type: 'paragraph',
          content: [{ type: 'text', text: ZERO_WIDTH_SPACE }],
        }
      }
      return { type: 'paragraph' }
    }),
  }
}

// Markdownソースエディタから、生のMarkdown文字列を取り出す。
// 1行=1段落の内部表現をそのまま "\n" 区切りで連結した、保存形式（プレーン形式）の文字列になる。
export const getMarkdownFromSourceEditor = (editor: Editor): string => {
  return editor.getText({ blockSeparator: '\n' }).replace(/​/g, '')
}

// リッチエディタから、保存形式（プレーン形式）の Markdown を取り出す。
// - 先頭がテーブルのとき getMarkdown() が出力先頭に余分な改行を付けるため除去する
// - 空段落マーカー（&nbsp;/\xA0）を可視の空行へ、段落区切りを \n へ変換し、
//   旧実装と互換のプレーン形式（\n=改行、空行=可視の空行）にする
export const getMarkdownFromRichEditor = (editor: Editor): string => {
  return demoteMarkersToBlankLines(
    collapseEmptyParagraphMarkers(
      normalize(editor.getMarkdown().replace(/^\n+/, ''))
    )
  )
}

// リッチ表示（プレビュー含む）のコピーで、選択範囲を Markdown ソースとして
// text/plain に書く。ProseMirror 既定はブロック間を "\n\n" で連結するため、
// 空段落が Markdown ソースエディタへのペーストで空行3つに増殖してしまう。
// 保存形式と同じプレーン形式にすることで、ソースエディタの表示・保存値とも一致する。
export const serializeRichTextSliceForClipboard = (
  editor: Editor | null,
  slice: Slice
): string => {
  const fallbackText = () =>
    slice.content.textBetween(0, slice.content.size, '\n\n')
  if (!editor?.markdown) return fallbackText()
  try {
    const markdown = editor.markdown.serialize({
      type: 'doc',
      content: slice.content.toJSON(),
    })
    return demoteMarkersToBlankLines(
      collapseEmptyParagraphMarkers(normalize(markdown)).replace(/^\n+/, '')
    )
  } catch {
    // テーブルセル選択等で不完全なノード断片になった場合は既定のテキスト化に戻す
    return fallbackText()
  }
}

// marked は `\*` `\_` `\#` `` \` `` 等のバックスラッシュエスケープを escape トークンに分解するが、
// @tiptap/markdown の parseInlineTokens は escape トークンのハンドラを持たず、
// マーク扱いでも子トークンでもないため黙って破棄する。
// escape トークンを「エスケープ後の文字」のテキストノードとして拾い、文字の消失を防ぐ。
const MarkdownEscape = Extension.create({
  name: 'markdownEscape',
  markdownTokenName: 'escape',
  parseMarkdown: (token: MarkdownToken) => ({
    type: 'text',
    text: token.text || '',
  }),
})

// --- テーブルセル内改行の保持 ---
// 既定のテーブルシリアライザは collapseWhitespace(/\s+/ → " ") でセル内容を1行に潰すため、
// hardBreak（  \n）や段落境界が消失する。
// テキストに "<br>" を直接入れると encodeHtmlEntities で "&lt;br&gt;" にされるため、
// 私用領域のセンチネルへ置換してからシリアライズし、出力後に <br> へ復元する。

const TABLE_BR_SENTINEL = String.fromCharCode(0xe000, 0xe001, 0xe002, 0xe003)

const replaceHardBreaks = (node: JSONContent): JSONContent => {
  if (node.type === 'hardBreak') {
    return { type: 'text', text: TABLE_BR_SENTINEL }
  }
  if (!node.content) return node
  return { ...node, content: node.content.map(replaceHardBreaks) }
}

const flattenTableCellLineBreaks = (cell: JSONContent): JSONContent => {
  const blocks = (cell.content ?? []).map(replaceHardBreaks)
  if (blocks.length <= 1) return { ...cell, content: blocks }
  const inline: JSONContent[] = []
  blocks.forEach((block, index) => {
    if (index > 0) {
      inline.push({ type: 'text', text: TABLE_BR_SENTINEL })
    }
    inline.push(...(block.content ?? []))
  })
  return { ...cell, content: [{ type: 'paragraph', content: inline }] }
}

const normalizeTableCellLineBreaks = (node: JSONContent): JSONContent => {
  if (node.type === 'tableCell' || node.type === 'tableHeader') {
    return flattenTableCellLineBreaks(node)
  }
  if (!node.content) return node
  return { ...node, content: node.content.map(normalizeTableCellLineBreaks) }
}

const baseTableRenderMarkdown = Table.config.renderMarkdown

const CustomTable = Table.extend({
  renderMarkdown(
    node: JSONContent,
    helpers: MarkdownRendererHelpers,
    ctx: RenderContext
  ) {
    const rendered =
      baseTableRenderMarkdown?.(
        normalizeTableCellLineBreaks(node),
        helpers,
        ctx
      ) || ''
    return rendered.split(TABLE_BR_SENTINEL).join('<br>')
  },
})

const EMPTY_PARAGRAPH_MARKDOWN = '&nbsp;'

// stock の renderMarkdown は「2つ目以降の空段落」だけ &nbsp; を出力し、
// 1つ目は空文字に頼る。しかし parse 側は separatorCount-1 で計算するため
// 空行だけでは1つ目の空段落が消失する。常に &nbsp; を出力して往復を保証する。
const CustomParagraph = Paragraph.extend({
  renderMarkdown: (node, h) => {
    if (!node) return ''
    const content = Array.isArray(node.content) ? node.content : []
    if (content.length === 0 || content.every(n => n.type === 'hardBreak')) {
      return EMPTY_PARAGRAPH_MARKDOWN
    }
    return h.renderChildren(content)
  },
})

// stock の renderNestedMarkdownContent は hardBreak（  \n）後の継続行に
// リスト項目プレフィックス分のインデントを付与しない。
// そのため Markdown パーサーが継続行をリスト項目の外と解釈し、番号が消費される。
const CustomListItem = ListItem.extend({
  renderMarkdown: (node, h, ctx) => {
    if (!node || !Array.isArray(node.content)) return ''

    let prefix: string
    if (ctx?.parentType === 'orderedList') {
      const start = ctx.meta?.parentAttrs?.start || 1
      prefix = `${start + ctx.index}. `
    } else {
      prefix = '- '
    }

    const [content, ...children] = node.content
    const mainContent = h.renderChildren([content])

    const mainLines = mainContent.split('\n')
    const continuationIndent = ' '.repeat(prefix.length)
    let output = prefix + mainLines[0]
    for (let i = 1; i < mainLines.length; i++) {
      output += '\n' + (mainLines[i] ? continuationIndent + mainLines[i] : '')
    }

    children.forEach((child, i) => {
      const childContent =
        h.renderChild?.(child, i + 1) ?? h.renderChildren([child])
      if (childContent != null) {
        const indentedChild = childContent
          .split('\n')
          .map(line => h.indent(line))
          .join('\n')
        output +=
          child.type === 'paragraph'
            ? `\n\n${indentedChild}`
            : `\n${indentedChild}`
      }
    })

    return output
  },
})

const CustomCodeBlock = CodeBlockLowlight.extend({
  addKeyboardShortcuts() {
    return {
      'Mod-Shift-e': () => this.editor.commands.toggleCodeBlock(),
    }
  },
})

const CustomBlockquote = Blockquote.extend({
  addKeyboardShortcuts() {
    return {
      'Mod-Shift-.': () => this.editor.commands.toggleBlockquote(),
    }
  },
})

const CustomHeading = Heading.extend({
  addKeyboardShortcuts() {
    return {
      'Mod-Shift-1': () => this.editor.commands.toggleHeading({ level: 1 }),
      'Mod-Shift-2': () => this.editor.commands.toggleHeading({ level: 2 }),
      'Mod-Shift-3': () => this.editor.commands.toggleHeading({ level: 3 }),
      'Mod-Shift-4': () => this.editor.commands.toggleHeading({ level: 4 }),
      'Mod-Shift-5': () => this.editor.commands.toggleHeading({ level: 5 }),
      'Mod-Shift-6': () => this.editor.commands.toggleHeading({ level: 6 }),
    }
  },
})

const CustomHorizontalRule = HorizontalRule.extend({
  addKeyboardShortcuts() {
    return {
      'Mod-Shift-h': () => this.editor.commands.setHorizontalRule(),
    }
  },
})

export const splitHardBreaks = ({
  tr,
  state,
}: {
  tr: Transaction
  state: EditorState
}): boolean => {
  const { from, to } = state.selection
  const hardBreakPositions: number[] = []

  state.doc.nodesBetween(from, to, (node, pos) => {
    if (node.type.name === 'hardBreak') {
      hardBreakPositions.push(pos)
    }
  })

  for (let i = hardBreakPositions.length - 1; i >= 0; i--) {
    const pos = hardBreakPositions[i]
    tr.delete(pos, pos + 1)
    tr.split(pos)
  }

  return true
}

export const findHighLightItemsInRichText = (editor: Editor): string[] => {
  let items: string[] = []
  if (editor.isActive('heading', { level: 1 })) {
    items = [...items, StyleType.HEADER_ONE]
  }
  if (editor.isActive('heading', { level: 2 })) {
    items = [...items, StyleType.HEADER_TWO]
  }
  if (editor.isActive('heading', { level: 3 })) {
    items = [...items, StyleType.HEADER_THREE]
  }
  if (editor.isActive('heading', { level: 4 })) {
    items = [...items, StyleType.HEADER_FOUR]
  }
  if (editor.isActive('heading', { level: 5 })) {
    items = [...items, StyleType.HEADER_FIVE]
  }
  if (editor.isActive('heading', { level: 6 })) {
    items = [...items, StyleType.HEADER_SIX]
  }
  if (editor.isActive('bold')) items = [...items, StyleType.BOLD]
  if (editor.isActive('italic')) items = [...items, StyleType.ITALIC]
  if (editor.isActive('strike')) items = [...items, StyleType.STRIKETHROUGH]
  if (editor.isActive('code')) items = [...items, StyleType.CODE]
  if (editor.isActive('codeBlock')) items = [...items, StyleType.CODE_BLOCK]
  if (editor.isActive('blockquote')) items = [...items, StyleType.BLOCKQUOTE]
  if (editor.isActive('bulletList')) {
    items = [...items, StyleType.UNORDERED_LIST_ITEM]
  }
  if (editor.isActive('orderedList')) {
    items = [...items, StyleType.ORDERED_LIST_ITEM]
  }
  if (editor.isActive('horizontalRule')) {
    items = [...items, StyleType.HORIZONTAL_RULE]
  }
  if (editor.isActive('taskList')) items = [...items, StyleType.TASK_LIST]
  if (editor.isActive('table')) items = [...items, StyleType.TABLE]
  if (editor.isActive('image')) items = [...items, StyleType.FILE]
  return items
}

export const insertLinkFromClipboard = (
  clipboardData: DataTransfer,
  editor: Editor
): boolean => {
  const html = clipboardData?.getData(CLIPBOARD_DATA_TYPE_HTML)
  if (!isLinkHtmlPaste(html)) return false

  editor.commands.insertContent(html, {
    parseOptions: {
      preserveWhitespace: false,
    },
  })
  return true
}
